import { useState, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { FlyingCartProvider } from "./contexts/FlyingCartContext";
import { AuroraBackground } from "./components/AuroraBackground";
import { AnimatedRoutes } from "./components/AnimatedRoutes";
import { SplashScreen } from "./components/SplashScreen";
import { Onboarding } from "./components/Onboarding";
import { preloadCriticalResources } from "./hooks/usePrefetch";
import { useWebVitals, initWebVitalsOptimizations } from "./hooks/useWebVitals";

const queryClient = new QueryClient();

// Initialize Web Vitals optimizations immediately
if (typeof window !== 'undefined') {
  // Run LCP optimizations immediately
  initWebVitalsOptimizations();
  
  // Preload critical resources on idle
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(() => {
      preloadCriticalResources();
    }, { timeout: 1000 });
  } else {
    setTimeout(preloadCriticalResources, 500);
  }
}

const App = () => {
  const [showSplash, setShowSplash] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  
  // Monitor Web Vitals in development
  useWebVitals();

  useEffect(() => {
    const onboardingCompleted = localStorage.getItem("onboarding_completed");
    if (!onboardingCompleted) {
      setShowOnboarding(true);
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <CartProvider>
              <FlyingCartProvider>
                <TooltipProvider>
                  {showSplash && (
                    <SplashScreen onComplete={() => setShowSplash(false)} />
                  )}
                  {!showSplash && showOnboarding && (
                    <Onboarding onComplete={() => setShowOnboarding(false)} />
                  )}
                  <AuroraBackground />
                  <Toaster />
                  <Sonner />
                  <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                    <AnimatedRoutes />
                  </BrowserRouter>
                </TooltipProvider>
              </FlyingCartProvider>
            </CartProvider>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
